import { videos } from '@/mocks/videos';
import type { VideoItem, VideoType } from '@/types/video';

export function getAllVideos(): VideoItem[] {
  return videos;
}

export function getVideoById(id: string): VideoItem | undefined {
  return videos.find((v) => v.id === id);
}

export function getVideosByType(type: VideoType): VideoItem[] {
  return videos.filter((v) => v.type === type);
}

export function getLatestVideos(limit = 12): VideoItem[] {
  return [...videos]
    .sort((a, b) => b.year - a.year || b.rating - a.rating)
    .slice(0, limit);
}

export function getHotVideos(limit = 8): VideoItem[] {
  return [...videos].sort((a, b) => b.playCount - a.playCount).slice(0, limit);
}

export interface VideoFilter {
  type?: VideoType;
  region?: string;
  year?: string;
  sort?: 'latest' | 'hot';
}

export function filterVideos({ type, region, year, sort = 'latest' }: VideoFilter): VideoItem[] {
  let list = type ? videos.filter((v) => v.type === type) : [...videos];
  if (region && region !== '全部') {
    list = list.filter((v) => v.region === region);
  }
  if (year && year !== '全部') {
    list = list.filter((v) => String(v.year) === year);
  }
  if (sort === 'hot') {
    list = [...list].sort((a, b) => b.playCount - a.playCount);
  } else {
    list = [...list].sort((a, b) => b.year - a.year || b.rating - a.rating);
  }
  return list;
}

export function searchVideos(keyword: string): VideoItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return videos.filter((v) => {
    const haystack = [v.title, v.region, v.director, ...v.tags, ...v.cast, String(v.year)]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedVideos(video: VideoItem, limit = 6): VideoItem[] {
  const related = videos.filter((v) => {
    if (v.id === video.id) return false;
    const sameTag = v.tags.some((t) => video.tags.includes(t));
    const sameCast = v.cast.some((c) => video.cast.includes(c));
    return v.type === video.type || sameTag || sameCast;
  });
  return related
    .sort((a, b) => {
      const aScore = a.tags.filter((t) => video.tags.includes(t)).length + (a.type === video.type ? 1 : 0);
      const bScore = b.tags.filter((t) => video.tags.includes(t)).length + (b.type === video.type ? 1 : 0);
      return bScore - aScore || b.rating - a.rating;
    })
    .slice(0, limit);
}

export function formatPlayCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)} 万次播放`;
  }
  return `${count} 次播放`;
}

export const typeNameMap: Record<VideoType, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
};

export function isValidType(value: string | undefined): value is VideoType {
  return value === 'movie' || value === 'tv' || value === 'variety';
}

export const PAGE_SIZE = 8;